import { useMemo, useState } from "react";
import { MessageCircle, Crosshair, Eye } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import { openWa, waLink } from "@/lib/wa";

export default function Order() {
  const { t, pick } = useLang();
  const { services, neighborhoods } = useData();

  const [f, setF] = useState({ name: "", phone: "", city: "الرياض", hood: "", make: "", model: "", year: "", service: "", size: "", desc: "" });
  const [shareLoc, setShareLoc] = useState(false);
  const [locStatus, setLocStatus] = useState("");
  const [errors, setErrors] = useState([]);

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const hoodName = useMemo(() => pick((neighborhoods.find((n) => n.slug === f.hood) || {}).name_ar, (neighborhoods.find((n) => n.slug === f.hood) || {}).name_en) || "", [f.hood, neighborhoods, pick]);
  const serviceName = useMemo(() => pick((services.find((s) => s.slug === f.service) || {}).title_ar, (services.find((s) => s.slug === f.service) || {}).title_en) || "", [f.service, services, pick]);

  const needsSize = ["tire-change", "balancing", "tire-size-help"].includes(f.service);
  const needsDesc = ["puncture-repair", "other-services"].includes(f.service);

  const message = useMemo(() => {
    const lines = [
      "السلام عليكم، أرغب في طلب خدمة من ميرو للإطارات وخدمات السيارات.",
      `الخدمة المطلوبة: ${serviceName || "____"}.`,
      `نوع السيارة: ${f.make || "____"}.`,
      `موديل السيارة: ${f.model || "____"}.`,
    ];
    if (f.year) lines.push(`سنة الصنع: ${f.year}.`);
    if (needsSize) lines.push(`مقاس الإطار: ${f.size || "____"}.`);
    lines.push(`المدينة: ${f.city || "الرياض"}.`, `الحي: ${hoodName || "____"}.`);
    if (f.desc) lines.push(`وصف المشكلة: ${f.desc}.`);
    if (f.name) lines.push(`اسم العميل: ${f.name}.`);
    if (f.phone) lines.push(`رقم التواصل: ${f.phone}.`);
    lines.push("أرجو توضيح التوفر والتكلفة.");
    return lines.join("\n");
  }, [f, hoodName, serviceName, needsSize]);

  const validate = () => {
    const missing = [];
    if (!f.service) missing.push(t("order.service"));
    if (!f.hood) missing.push(t("order.hood"));
    if (!f.make) missing.push(t("order.make"));
    if (!f.model) missing.push(t("order.model"));
    if (needsSize && !f.size) missing.push(t("order.size"));
    if (f.service === "puncture-repair" && !f.desc) missing.push(t("order.desc"));
    setErrors(missing);
    return missing.length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    if (shareLoc && navigator.geolocation) {
      setLocStatus("loading");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocStatus("");
          const loc = `\nموقعي: https://maps.google.com/?q=${pos.coords.latitude},${pos.coords.longitude}`;
          window.open(waLink(message + loc), "_blank", "noopener");
        },
        () => {
          setLocStatus("err");
          openWa(message);
        },
        { timeout: 10000 }
      );
    } else {
      openWa(message);
    }
  };

  return (
    <main>
      <SEO title="اطلب خدمة ميرو | نموذج طلب خدمات الإطارات المتنقلة في الرياض" description="نموذج طلب خدمة ميرو للإطارات والبنشر المتنقل وحساسات الكفرات في الرياض: املأ الحقول وسيُفتح واتساب برسالة طلب منسقة إلى 0507359999." path="/order" />
      <PageHero eyebrow="ORDER SERVICE" title={t("order.title")} sub={t("order.sub")} />

      <section className="pb-24">
        <div className="mx-auto grid max-w-5xl gap-10 px-5 lg:grid-cols-[1.5fr_1fr] lg:px-8">
          <Reveal>
            <div className="card-glass p-7 sm:p-9" data-testid="order-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div><label className="mb-2 block text-sm font-bold text-zinc-200">{t("order.name")}</label><input className="field" value={f.name} onChange={set("name")} data-testid="order-name-input" /></div>
                <div><label className="mb-2 block text-sm font-bold text-zinc-200">{t("order.phone")}</label><input className="field" dir="ltr" value={f.phone} onChange={set("phone")} data-testid="order-phone-input" /></div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-zinc-200">{t("order.city")} *</label>
                  <input className="field" value={f.city} onChange={set("city")} data-testid="order-city-input" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-gold">{t("order.hood")} *</label>
                  <select className="field cursor-pointer" value={f.hood} onChange={set("hood")} data-testid="order-hood-select">
                    <option value="">{t("order.hoodPh")}</option>
                    {neighborhoods.map((n) => <option key={n.slug} value={n.slug}>{pick(n.name_ar, n.name_en)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-gold">{t("order.make")} *</label>
                  <input className="field" placeholder={t("order.makePh")} value={f.make} onChange={set("make")} data-testid="order-make-input" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-gold">{t("order.model")} *</label>
                  <input className="field" placeholder={t("order.modelPh")} value={f.model} onChange={set("model")} data-testid="order-model-input" />
                </div>
                <div><label className="mb-2 block text-sm font-bold text-zinc-200">{t("order.year")}</label><input className="field" dir="ltr" value={f.year} onChange={set("year")} data-testid="order-year-input" /></div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-gold">{t("order.service")} *</label>
                  <select className="field cursor-pointer" value={f.service} onChange={set("service")} data-testid="order-service-select">
                    <option value="">{t("order.servicePh")}</option>
                    {services.map((s) => <option key={s.slug} value={s.slug}>{pick(s.title_ar, s.title_en)}</option>)}
                  </select>
                </div>
                {needsSize && (
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-bold text-gold">{t("order.size")} *</label>
                    <input className="field" dir="ltr" placeholder={t("order.sizePh")} value={f.size} onChange={set("size")} data-testid="order-size-input" />
                  </div>
                )}
                {(needsDesc || f.desc) && (
                  <div className="sm:col-span-2">
                    <label className={`mb-2 block text-sm font-bold ${f.service === "puncture-repair" ? "text-gold" : "text-zinc-200"}`}>{t("order.desc")}{f.service === "puncture-repair" ? " *" : ""}</label>
                    <textarea className="field min-h-[90px]" placeholder={t("order.descPh")} value={f.desc} onChange={set("desc")} data-testid="order-desc-input" />
                  </div>
                )}
              </div>

              <label className="mt-6 flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-ink/60 p-4">
                <input type="checkbox" checked={shareLoc} onChange={(e) => setShareLoc(e.target.checked)} className="h-4 w-4 accent-[#FFC400]" data-testid="order-location-checkbox" />
                <Crosshair size={16} className="text-gold" />
                <span className="text-sm text-zinc-300">{t("order.shareLoc")}</span>
              </label>
              {locStatus === "loading" && <p className="mt-3 text-xs text-gold">{t("order.gettingLoc")}</p>}
              {locStatus === "err" && <p className="mt-3 text-xs text-[#f87171]">{t("order.locError")}</p>}

              {errors.length > 0 && (
                <div className="mt-5 rounded-2xl border border-[#EF4444]/40 bg-[#EF4444]/10 p-4 text-sm text-[#f87171]" data-testid="order-error-box">
                  {t("order.errRequired")}{errors.join("، ")}
                </div>
              )}

              <button onClick={submit} className="btn-wa mt-7 w-full !py-4 text-base" data-testid="order-submit-button">
                <MessageCircle size={19} /> {t("order.submit")}
              </button>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="sticky top-24 rounded-3xl border border-gold/25 bg-coal/60 p-7">
              <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-white"><Eye size={17} className="text-gold" /> {t("order.preview")}</h2>
              <pre className="whitespace-pre-wrap rounded-2xl bg-ink/80 p-5 text-[13px] leading-7 text-zinc-300" dir="rtl" data-testid="order-message-preview">{message}</pre>
              <p className="mt-4 text-[11px] leading-6 text-dim">{t("home.composerHint")}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
